import { memo, useMemo } from 'react';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { AUTOCOMPLETE_SX } from './filterStyles';
import { toArray } from './filterUtils';

export const NO_VARIANT_SENTINEL = '__NO_VARIANT__';
const NO_VARIANT_LABEL = 'No Variant Selected';

interface ProjectVariantMultiSelectProps {
    /** Available variant labels from the API (should be pre-sorted) */
    variantLabels: string[];
    /** Currently selected values (labels or NO_VARIANT_SENTINEL) */
    value: string[] | undefined;
    /** Called when selection changes */
    onChange: (values: string[] | undefined) => void;
    /** Keep dropdown inside the DOM tree (required when used inside a modal with focus traps) */
    disablePortal?: boolean;
}

function getDisplayLabel(value: string): string {
    return value === NO_VARIANT_SENTINEL ? NO_VARIANT_LABEL : value;
}

function ProjectVariantMultiSelectComponent({
    variantLabels,
    value,
    onChange,
    disablePortal,
}: ProjectVariantMultiSelectProps) {
    const options = useMemo(() => [NO_VARIANT_SENTINEL, ...variantLabels], [variantLabels]);

    const selectedValues = useMemo(() => {
        const values = toArray(value);
        return values.filter(v => options.includes(v));
    }, [options, value]);

    return (
        <Autocomplete
            multiple
            size="small"
            disablePortal={disablePortal}
            sx={{ minWidth: 220, maxWidth: 350, ...AUTOCOMPLETE_SX }}
            options={options}
            value={selectedValues}
            onChange={(_, values) => onChange(values.length > 0 ? values : undefined)}
            getOptionLabel={getDisplayLabel}
            renderInput={params => <TextField {...params} label="Variant" />}
            renderValue={(values, getItemProps) =>
                values.map((val, index) => {
                    const { key, ...tagProps } = getItemProps({ index });
                    return <Chip key={key} label={getDisplayLabel(val)} size="small" {...tagProps} />;
                })
            }
            slotProps={{
                popper: {
                    style: { width: 'fit-content' },
                },
            }}
        />
    );
}

export const ProjectVariantMultiSelect = memo(
    ProjectVariantMultiSelectComponent,
) as typeof ProjectVariantMultiSelectComponent;
